/**
 * window.QplayerReels - a façade over the element's own openReels/closeReels.
 *
 *   QplayerReels.openReels({ playlistId: 'PL1', mediaId: 'ME1', trigger: event.currentTarget });
 *   QplayerReels.closeReels();
 */

const REELS_ELEMENT = 'qplayer-reels-embed';

const allElements = () => Array.from(document.querySelectorAll(REELS_ELEMENT));

/** Named by data-qplayer-id, or the only one on the page. Unnamed with several, it says so. */
const resolveElement = (qplayerId) => {
	if (qplayerId) {
		return document.querySelector(`${REELS_ELEMENT}[data-qplayer-id="${qplayerId}"]`);
	}
	const elements = allElements();
	if (elements.length > 1) {
		const ids = elements.map((el) => el.dataset?.qplayerId || '(no data-qplayer-id)').join(', ');
		console.warn(`[QPlayer] this page has ${elements.length} <${REELS_ELEMENT}> (${ids}); pass qplayerId to say which`);
	}
	return elements[0] ?? null;
};

/**
 * @param {string} [options.playlistId] Defaults to the element's own.
 * @param {string} [options.mediaId]    Falls back to the first clip.
 * @param {Element} [options.trigger]   What was clicked; focus returns here on close.
 * @param {string} [options.qplayerId]  Which feed, when there is more than one.
 */
export const openReels = (options = {}) => {
	const element = resolveElement(options.qplayerId);
	if (!element) {
		console.warn(`[QPlayer] openReels found no <${REELS_ELEMENT}> on the page`);
		return;
	}
	if (typeof element.openReels !== 'function') {
		// In the DOM but not yet mounted - a real race for an inline script.
		console.warn(`[QPlayer] <${REELS_ELEMENT}> is not ready yet`);
		return;
	}
	element.openReels(options);
};

/** With no id, closes every feed: resolving to the first was a silent no-op. */
export const closeReels = (qplayerId) => {
	if (qplayerId) {
		resolveElement(qplayerId)?.closeReels?.();
		return;
	}
	allElements().forEach((element) => element.closeReels?.());
};

/** At bundle load, so an inline script can call it without waiting for React. Merged, not replaced. */
export const registerReelsApi = () => {
	if (typeof window === 'undefined') {
		return;
	}
	window.QplayerReels = { ...(window.QplayerReels || {}), openReels, closeReels };
};
